import { useEffect, useState } from 'react';

import { User, useCreateNewUserAccount } from '@givebacks/api/core';
import axios, { AxiosError } from 'axios';
import { getErrorMessage_v2 } from 'core/Utils';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Grid,
  Group,
  PasswordWithStrength,
  Stack,
  Text,
  TextInput,
  validatePassword,
} from 'ui';
import { isEmail, isNotEmpty, useForm } from 'ui/Form';

import { onboardingReferralAtom, resetOnboardingAtoms } from '../../../atoms/onboarding';
import { useOnboardingReferrals } from '../hooks';

type Props = {
  onClose?: () => void;
  setIsSignIn: (isSignIn: boolean) => void;
  onSuccessfulSignUp?: (user: User) => void;
};

const errorMessages = {
  first_name: 'First Name is required',
  last_name: 'Last Name is required',
  email: 'A valid email is required',
  phone_number: 'A valid phone number is required',
};

const CreateAccountForm = ({ onClose, setIsSignIn, onSuccessfulSignUp }: Props) => {
  const referralCode = useAtomValue(onboardingReferralAtom);
  const clearOnboardingCodes = useSetAtom(resetOnboardingAtoms);
  const { referredCauseId } = useOnboardingReferrals();
  const [searchParams] = useSearchParams();
  const { mutate: createAccount, isPending: isLoading } = useCreateNewUserAccount();

  const form = useForm({
    initialValues: {
      email: searchParams.get('email') || '',
      password: '',
      first_name: searchParams.get('firstName') || '',
      last_name: searchParams.get('lastName') || '',
    },
    validate: {
      first_name: isNotEmpty(''),
      last_name: isNotEmpty(''),
      email: isEmail(),
      password: validatePassword,
    },
  });

  const [error, setError] = useState('');
  const errorOne = Object.keys(form.errors)[0] as keyof typeof errorMessages;

  const handleCreateAccount = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!form.validate().hasErrors) {
      const request = {
        user: {
          email: form.values.email,
          password: form.values.password,
          first_name: form.values.first_name,
          last_name: form.values.last_name,
        },
      };
      referredCauseId && Object.assign(request.user, { cause: referredCauseId });
      referralCode && Object.assign(request.user, { signup_referral_code: referralCode });

      createAccount(request, {
        onError: (e) => {
          const error = e as Error | AxiosError;
          if (axios.isAxiosError(error)) {
            setError(getErrorMessage_v2(e));
          }
        },
        onSuccess: (user) => {
          clearOnboardingCodes();
          onSuccessfulSignUp?.(user);
          onClose?.();
        },
      });
    }
  };

  useEffect(() => {
    if (errorOne) {
      setError(errorMessages[errorOne]);
    } else setError('');
  }, [errorOne]);

  return (
    <form>
      <Stack justify="space-between">
        <Grid grow gutter={8} w="100%" mih={268}>
          <Grid.Col span={6}>
            <TextInput
              placeholder="First Name"
              {...form.getInputProps('first_name')}
              data-autofocus
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput placeholder="Last Name" {...form.getInputProps('last_name')} />
          </Grid.Col>
          <Grid.Col span={12}>
            <TextInput placeholder="Email Address" {...form.getInputProps('email')} />
          </Grid.Col>
          <Grid.Col span={12}>
            <PasswordWithStrength
              placeholder="Create a Password"
              {...form.getInputProps('password')}
            />
          </Grid.Col>
        </Grid>

        <Alert.Collapse in={!!error} title={error} type="error" />
        <Stack>
          <Button
            variant="gradient"
            onClick={handleCreateAccount}
            fullWidth
            disabled={!!errorOne}
            loading={isLoading}
            type="submit"
          >
            Create Account
          </Button>
          <Group justify="center" gap={0}>
            <Text fz="sm" c="gray">
              Already have an account?
            </Text>
            <Button variant="transparent" size="compact-sm" onClick={() => setIsSignIn(true)}>
              Sign In
            </Button>
          </Group>
        </Stack>
      </Stack>
    </form>
  );
};

export { CreateAccountForm };
